import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { ApiHttpError } from '@/api/client';
import {
  getDuelVotes,
  getUniqueVoters,
  getDuelVotesCountById,
  getDuelVotesCountBatch,
  voteOnDuel,
  type DuelVotesOrder,
} from '@/api/duelVotes';
import { updateProfileUiFlags } from '@/api/profiles';
import { MY_PROFILE_QUERY_KEY } from '@/constants';
import {
  REVOTE_CONFIRM_UI_FLAG_BY_KIND,
  readRevoteFlagFromStorage,
  type RevoteConfirmKind,
  type RevoteUiFlag,
  writeRevoteFlagToStorage,
} from '@/constants/revote';
import { trackUmamiEvent } from '@/helpers/analytics';
import { getAttributionSource } from '@/helpers/attribution';
import { clearFromBlogFlag, readFromBlogFlag } from '@/helpers/blog-attribution';
import { notify } from '@/services/notifications';
import { checkAndMaybeShowCountryModal } from '@/stores/countryPromptModal';
import { recordVotedDuelAndMaybeShowModal } from '@/stores/followXModal';
import { useRevoteConfirmModalStore } from '@/stores/revoteConfirmModal';
import type { DuelDto } from '@/types/duels';
import type {
  DuelVoteDto,
  DuelVotesListResponse,
  UniqueVotersResponse,
  VotesCountResponse,
  VoteOnDuelRequest,
  VoteOnDuelResponse,
} from '@/types/duelVotes';
import type { ProfileDto } from '@/types/profiles';
import type { UiDuel } from './duels';

const VOTES_PAGE_SIZE = 20;

function toDetails(details: unknown): Record<string, unknown> {
  if (!details || typeof details !== 'object') return {};
  return details as Record<string, unknown>;
}

function toNumericDetail(details: Record<string, unknown>, key: string): number | undefined {
  const value = Number(details[key]);
  return Number.isFinite(value) ? value : undefined;
}

function toStringDetail(details: Record<string, unknown>, key: string): string | undefined {
  const value = details[key];
  return typeof value === 'string' ? value : undefined;
}

function buildRevoteEventPayloadFromDetails(duelId: string, details: Record<string, unknown>) {
  return {
    duel_id: duelId,
    gw_id: typeof details.gw_id === 'string' ? details.gw_id : undefined,
    seconds_to_deadline: toNumericDetail(details, 'seconds_to_deadline'),
    pick_change_count_after: toNumericDetail(details, 'pick_change_count_after'),
  };
}

function buildRevoteEventPayloadFromResponse(
  duelId: string,
  revote:
    | {
        gw_id: string;
        seconds_to_deadline: number;
        pick_change_count_after: number;
      }
    | undefined,
) {
  return {
    duel_id: duelId,
    gw_id: revote?.gw_id,
    seconds_to_deadline: revote?.seconds_to_deadline,
    pick_change_count_after: revote?.pick_change_count_after,
  };
}

function isRevoteFlagEnabled(profile: ProfileDto | undefined, flag: RevoteUiFlag): boolean {
  return profile?.ui_flags?.[flag] === true || readRevoteFlagFromStorage(flag);
}

export function useDuelVotes(params: { duelId?: string; userId?: string; order?: DuelVotesOrder; limit?: number; offset?: number; enabled?: boolean }) {
  const stableParams = useMemo(() => ({
    duelId: params.duelId,
    userId: params.userId,
    order: params.order,
    limit: params.limit,
    offset: params.offset,
  }), [params.duelId, params.userId, params.order, params.limit, params.offset]);
  return useQuery<DuelVotesListResponse>({
    queryKey: ['duelVotes', 'list', stableParams],
    queryFn: () => getDuelVotes({
      duel_id: stableParams.duelId,
      user_id: stableParams.userId,
      order: stableParams.order,
      limit: stableParams.limit,
      offset: stableParams.offset,
    }),
    enabled: params.enabled !== undefined ? params.enabled : !!(stableParams.duelId || stableParams.userId),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

interface UseInfiniteDuelVotesForPlayerParams {
  duelId: string;
  playerId: string;
  order?: DuelVotesOrder;
  limit?: number;
  enabled?: boolean;
}

export function useInfiniteDuelVotesForPlayer(params: UseInfiniteDuelVotesForPlayerParams) {
  const { duelId, playerId, order = 'created_at.desc', limit = VOTES_PAGE_SIZE, enabled = true } = params;

  // Ensure we have valid IDs (not empty strings)
  const hasValidIds = !!duelId && duelId.length > 0 && !!playerId && playerId.length > 0;

  const query = useInfiniteQuery({
    queryKey: ['duelVotes', 'infinite', { duelId, playerId, order, limit }],
    queryFn: async ({ pageParam = 0 }) => {
      return await getDuelVotes({
        duel_id: duelId,
        selected_player_id: playerId,
        order,
        limit,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { offset, limit: pageLimit, total } = lastPage.pagination;
      // If total is available, use it; otherwise check if we got a full page
      if (total !== undefined) {
        const nextOffset = offset + pageLimit;
        return nextOffset < total ? nextOffset : undefined;
      }
      // Fallback: if we got fewer items than limit, we've reached the end
      if (lastPage.duel_votes.length < pageLimit) {
        return undefined;
      }
      return offset + pageLimit;
    },
    enabled: enabled && hasValidIds,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Flatten all pages into a single array
  const votes: DuelVoteDto[] = query.data?.pages.flatMap((page) => page.duel_votes) ?? [];

  // Get total count from the first page, or count loaded items if total not available
  const totalCount = query.data?.pages[0]?.pagination.total ?? votes.length;

  return {
    votes,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
  };
}

export function useUniqueVoters() {
  return useQuery<UniqueVotersResponse>({
    queryKey: ['duelVotes', 'uniqueVoters'],
    queryFn: () => getUniqueVoters(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDuelVotesCount(duelId?: string) {
  return useQuery<VotesCountResponse>({
    queryKey: ['duelVotes', 'count', duelId],
    queryFn: () => getDuelVotesCountById(duelId as string),
    enabled: !!duelId,
    staleTime: 15 * 1000,
  });
}

export function useDuelVotesCounts(duelIds: string[]) {
  const ids = (duelIds || []).filter(Boolean);
  return useQuery<VotesCountResponse>({
    queryKey: ['duelVotes', 'counts', ids],
    queryFn: () => getDuelVotesCountBatch(ids),
    enabled: ids.length > 0,
    staleTime: 15 * 1000,
  });
}

export function useVoteOnDuel() {
  const queryClient = useQueryClient();
  const requestRevoteConfirmation = useRevoteConfirmModalStore((s) => s.request);

  const persistRevoteFlag = useCallback(
    async (flag: RevoteUiFlag) => {
      writeRevoteFlagToStorage(flag);
      try {
        const response = await updateProfileUiFlags({ flag });
        queryClient.setQueryData(MY_PROFILE_QUERY_KEY, response.data);
      } catch {
        // Fire-and-forget preference sync. Local fallback is already written.
      }
    },
    [queryClient],
  );

  const mutation = useMutation<VoteOnDuelResponse, unknown, VoteOnDuelRequest>({
    mutationFn: voteOnDuel,
    onSuccess: (res, variables) => {
      const revote = res.data?.revote;

      if (variables.selected_player_id === null) {
        notify.voting.removed();
      } else if (revote?.state === 'changed') {
        if (!variables.confirm_kind) {
          trackUmamiEvent('revote_attempt', buildRevoteEventPayloadFromResponse(variables.duel_id, revote));
        }
        notify.voting.pickUpdated(revote.changes_left);
        trackUmamiEvent('revote_success', buildRevoteEventPayloadFromResponse(variables.duel_id, revote));
      } else if (revote?.state === 'initial' || !revote) {
        notify.voting.submitted();
      }

      // Only consume the blog attribution flag on an actual vote, not on vote removal
      const isVote = variables.selected_player_id !== null && revote?.state !== 'unchanged';
      const fromBlog = isVote ? readFromBlogFlag() : false;
      trackUmamiEvent('duel_vote_success', {
        duelId: variables.duel_id,
        selectedPlayerId: variables.selected_player_id,
        source: getAttributionSource(),
        fromBlog: fromBlog ? fromBlog.slug : 'none',
      });
      if (fromBlog) clearFromBlogFlag();

      // 1. If the API returned updated stats, update the duels cache immediately with the fresh data
      if (res.data?.votes && res.data.duel_id) {
        const { duel_id, votes, potential_rating, rating_factors, vote_changes_count, last_change_at, had_early_bonus_on_current_pick } = res.data;

        // Map rating_factors snake_case → camelCase for UiDuel
        const ratingFactors = rating_factors
          ? {
            duelWeight: rating_factors.duel_weight,
            duelWeightDetail: rating_factors.duel_weight_detail,
            earlyBonusValue: rating_factors.early_bonus_value,
            earlyBonusDetail: rating_factors.early_bonus_detail,
            isDuelOfTheWeek: rating_factors.is_duel_of_the_week,
            ratingFactorsDebug: rating_factors.rating_factors_debug,
            humanVotesPlayerA: votes.player_a_votes,
            humanVotesPlayerB: votes.player_b_votes,
          }
          : undefined;

        // Helper to update a single duel
        const updateDuel = (duel: UiDuel): UiDuel => {
          if (duel.id !== duel_id) return duel;
          return {
            ...duel,
            votes: {
              ...duel.votes,
              total_votes: votes.total_votes,
              player1_votes: votes.player_a_votes,
              player2_votes: votes.player_b_votes,
              pick_change_count: vote_changes_count ?? duel.votes.pick_change_count,
              last_change_at: last_change_at ?? duel.votes.last_change_at,
              had_early_bonus_on_current_pick:
                had_early_bonus_on_current_pick ?? duel.votes.had_early_bonus_on_current_pick,
            },
            selectedPlayerId: votes.user_vote === duel.playerAId
              ? 'player1'
              : votes.user_vote === duel.playerBId
                ? 'player2'
                : null,
            ...(potential_rating && { potential_rating }),
            ...(ratingFactors && { ratingFactors }),
          };
        };

        // Update all 'duels' lists in cache (UiDuel format) - supports both old array format and new infinite query format
        queryClient.setQueriesData({ queryKey: ['duels'] }, (oldData: unknown) => {
          if (!oldData) return oldData;

          // Handle infinite query format: { pages: [{ duels: [], pagination: {} }, ...], pageParams: [] }
          if (typeof oldData === 'object' && 'pages' in (oldData as Record<string, unknown>)) {
            const infiniteData = oldData as { pages: Array<{ duels: UiDuel[]; pagination: unknown }>; pageParams: unknown[] };
            return {
              ...infiniteData,
              pages: infiniteData.pages.map((page) => ({
                ...page,
                duels: page.duels.map(updateDuel),
              })),
            };
          }

          // Handle old array format
          if (Array.isArray(oldData)) {
            return (oldData as UiDuel[]).map(updateDuel);
          }

          // Handle old object format: { duels: [] }
          if (typeof oldData === 'object' && 'duels' in (oldData as Record<string, unknown>)) {
            const objData = oldData as { duels: UiDuel[] };
            return { ...objData, duels: objData.duels.map(updateDuel) };
          }

          return oldData;
        });

        // Update all 'duel' detail queries (both by id and slug) (DuelDto format)
        queryClient.setQueriesData({ queryKey: ['duel'] }, (oldData: DuelDto | undefined) => {
          if (!oldData) return oldData;
          if (oldData.id !== duel_id) return oldData; // Skip if not the same duel

          return {
            ...oldData,
            votes: {
              ...(oldData.votes ?? {}),
              player_a_votes: votes.player_a_votes,
              player_b_votes: votes.player_b_votes,
              total_votes: votes.total_votes,
              user_vote: votes.user_vote,
              pick_change_count: vote_changes_count ?? oldData.votes?.pick_change_count,
              last_change_at: last_change_at ?? oldData.votes?.last_change_at,
              had_early_bonus_on_current_pick:
                had_early_bonus_on_current_pick ?? oldData.votes?.had_early_bonus_on_current_pick,
            },
            ...(potential_rating && { potential_rating }),
            ...(rating_factors && { rating_factors }),
          } as DuelDto;
        });
      }

      // 2. Invalidate vote-related queries. Do NOT refetch the main duels list:
      // vote counts are already patched in cache; a total_votes.desc refetch would
      // reshuffle pages and jump cards. Filtered my_vote / my_duels views still need sync.
      queryClient.invalidateQueries({ queryKey: ['duelVotes'] });

      if (variables.duel_id) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            if (query.queryKey[0] !== 'duels') return false;
            const filters = query.queryKey[1] as { myVote?: string; myDuels?: boolean } | undefined;
            return filters?.myVote != null || filters?.myDuels === true;
          },
        });
        queryClient.invalidateQueries({ queryKey: ['duel'] });
        queryClient.invalidateQueries({ queryKey: ['duelVotes', 'count', variables.duel_id] });
        queryClient.invalidateQueries({ queryKey: ['duelVotes', 'uniqueVoters'] });

        // Refetch duel detail to refresh potential_rating and rating_factors (duels list already updated optimistically)
        queryClient.refetchQueries({
          queryKey: ['duel'],
          type: 'active',
        });
      }

      // Maybe show Follow X modal after 5th unique vote
      recordVotedDuelAndMaybeShowModal(variables.duel_id, variables.selected_player_id, queryClient);

      // Refetch profile so total_predictions is fresh, then maybe show country modal after 20th vote
      queryClient.refetchQueries({ queryKey: MY_PROFILE_QUERY_KEY }).then(() => {
        checkAndMaybeShowCountryModal(queryClient);
      });
    },
    onError: (error, variables) => {
      const apiError = error as ApiHttpError;
      const details = toDetails(apiError.details);
      const duelId = variables?.duel_id ?? '';

      if (apiError.status === 429) {
        const message = apiError.message || 'Rate limit exceeded. Please slow down.';
        notify.warning(message);
      } else if (apiError.status === 403 && apiError.code === 'EMAIL_NOT_VERIFIED') {
        const message = apiError.message || 'Email verification required. Please verify your email to vote.';
        notify.warning(message);
      } else if (apiError.code === 'REVOTE_LIMIT_REACHED') {
        const payload = buildRevoteEventPayloadFromDetails(duelId, details);
        trackUmamiEvent('revote_attempt', payload);
        trackUmamiEvent('revote_blocked_limit', payload);
        notify.voting.revoteNoChangesLeft();
      } else if (apiError.code === 'REVOTE_FINAL5_LIMIT') {
        const payload = buildRevoteEventPayloadFromDetails(duelId, details);
        trackUmamiEvent('revote_attempt', payload);
        trackUmamiEvent('revote_blocked_final5', payload);
        notify.voting.revoteFinal5Limit();
      } else if (apiError.code === 'REVOTE_COOLDOWN_ACTIVE') {
        const payload = buildRevoteEventPayloadFromDetails(duelId, details);
        trackUmamiEvent('revote_attempt', payload);
        trackUmamiEvent('revote_blocked_cooldown', payload);
        const secondsRemaining = toNumericDetail(details, 'seconds_remaining') ?? 1;
        notify.voting.revoteCooldown(secondsRemaining);
      } else if (apiError.code === 'REVOTE_CONFIRM_REQUIRED') {
        const payload = buildRevoteEventPayloadFromDetails(duelId, details);
        trackUmamiEvent('revote_attempt', payload);
      }
    },
  });

  const submitVote = useCallback(
    async (request: VoteOnDuelRequest): Promise<VoteOnDuelResponse | null> => {
      try {
        return await mutation.mutateAsync(request);
      } catch (error) {
        const apiError = error as ApiHttpError;
        const details = toDetails(apiError.details);
        const confirmKind = toStringDetail(details, 'confirm_kind') as RevoteConfirmKind | undefined;

        if (apiError.code !== 'REVOTE_CONFIRM_REQUIRED' || !confirmKind || request.selected_player_id === null) {
          throw error;
        }

        const uiFlag = REVOTE_CONFIRM_UI_FLAG_BY_KIND[confirmKind];
        const profile = queryClient.getQueryData<ProfileDto>(MY_PROFILE_QUERY_KEY);
        const payload = buildRevoteEventPayloadFromDetails(request.duel_id, details);
        const earlyBonusFrom = toNumericDetail(details, 'early_bonus_from');
        const earlyBonusTo = toNumericDetail(details, 'early_bonus_to');
        const earlyBonusWindow = toStringDetail(details, 'early_bonus_window');
        const bonus =
          earlyBonusFrom !== undefined && earlyBonusTo !== undefined
            ? {
                from: earlyBonusFrom,
                to: earlyBonusTo,
                ...(earlyBonusWindow ? { window: earlyBonusWindow } : {}),
              }
            : null;
        const payloadWithType = {
          ...payload,
          confirm_type: confirmKind,
          early_bonus_from: earlyBonusFrom,
          early_bonus_to: earlyBonusTo,
          early_bonus_window: earlyBonusWindow,
        };

        if (isRevoteFlagEnabled(profile, uiFlag)) {
          trackUmamiEvent('revote_confirm_confirmed', { ...payloadWithType, auto_confirmed: true });
          return await mutation.mutateAsync({ ...request, confirm_kind: confirmKind });
        }

        trackUmamiEvent('revote_confirm_shown', payloadWithType);
        const decision = await requestRevoteConfirmation({ kind: confirmKind, bonus });

        if (!decision.confirmed) {
          trackUmamiEvent('revote_confirm_cancelled', payloadWithType);
          return null;
        }

        trackUmamiEvent('revote_confirm_confirmed', payloadWithType);
        const result = await mutation.mutateAsync({ ...request, confirm_kind: confirmKind });

        // Persist "don't show again" only after a successful confirmed change.
        if (decision.dontShow) {
          await persistRevoteFlag(uiFlag);
        }

        return result;
      }
    },
    [mutation, persistRevoteFlag, queryClient, requestRevoteConfirmation],
  );

  return {
    ...mutation,
    submitVote,
  };
}

